import { createContext, forwardRef, useContext, type ComponentRef, type ReactNode } from "react";

import { cn } from "../../lib";
import { Box } from "../../primitives/box";
import {
  DropdownMenuCheckboxItem as DropdownMenuCheckboxItemPrimitive,
  DropdownMenuContent as DropdownMenuContentPrimitive,
  DropdownMenuGroup as DropdownMenuGroupPrimitive,
  DropdownMenuItemIndicator as DropdownMenuItemIndicatorPrimitive,
  DropdownMenuItem as DropdownMenuItemPrimitive,
  DropdownMenuPortal,
  DropdownMenuRadioGroup as DropdownMenuRadioGroupPrimitive,
  DropdownMenuRadioItem as DropdownMenuRadioItemPrimitive,
  DropdownMenuRoot,
  DropdownMenuSeparator as DropdownMenuSeparatorPrimitive,
  DropdownMenuSubContent as DropdownMenuSubContentPrimitive,
  DropdownMenuSub as DropdownMenuSubPrimitive,
  DropdownMenuSubTrigger as DropdownMenuSubTriggerPrimitive,
  DropdownMenuTrigger as DropdownMenuTriggerPrimitive,
} from "../../primitives/dropdown-menu";
import { iconSize } from "../../tokens";
import { Icon } from "../icons";
import { Typography } from "../typography";
import { dropdownContentEnterFadeMs } from "./content-look";
import {
  dropdownContentVariants,
  dropdownItemVariants,
  dropdownSeparatorClass,
} from "./dropdown.variants";
import { getItemAccessibilityLabel, getItemColor } from "./lib";
import type {
  DropdownCheckboxItemProps,
  DropdownContentProps,
  DropdownGroupProps,
  DropdownItemIconProps,
  DropdownItemProps,
  DropdownItemTextProps,
  DropdownRadioGroupProps,
  DropdownRadioItemProps,
  DropdownRootProps,
  DropdownSeparatorProps,
  DropdownSubContentProps,
  DropdownSubProps,
  DropdownSubTriggerProps,
  DropdownTriggerProps,
} from "./types";

const DropdownRoot = ({
  open,
  defaultOpen,
  onOpenChange,
  modal = true,
  children,
}: DropdownRootProps) => (
  <DropdownMenuRoot open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
    {children}
  </DropdownMenuRoot>
);

DropdownRoot.displayName = "DropdownRoot";

// Trigger and Content take a ref (ADR-007), e.g. to measure or focus the
// trigger; the item parts don't until a caller needs one
const DropdownTrigger = forwardRef<
  ComponentRef<typeof DropdownMenuTriggerPrimitive>,
  DropdownTriggerProps
>(({ children, ...rest }, ref) => (
  <DropdownMenuTriggerPrimitive ref={ref} {...rest}>
    {children}
  </DropdownMenuTriggerPrimitive>
));

DropdownTrigger.displayName = "DropdownTrigger";

const DropdownContent = forwardRef<
  ComponentRef<typeof DropdownMenuContentPrimitive>,
  DropdownContentProps
>(({ side = "bottom", children, className, ...rest }, ref) => (
  <DropdownMenuPortal>
    <DropdownMenuContentPrimitive
      ref={ref}
      side={side}
      className={cn(dropdownContentVariants(), className)}
      enterFadeMs={dropdownContentEnterFadeMs}
      // Tab and Shift+Tab close every menu and move focus on from its trigger (web)
      closeOnTab
      {...rest}
    >
      {children}
    </DropdownMenuContentPrimitive>
  </DropdownMenuPortal>
));

DropdownContent.displayName = "DropdownContent";

type ItemColor = ReturnType<typeof getItemColor>;

// The colour an item gives its label and its DropdownItemIcon / DropdownItemText
// children: destructive red, disabled attenuated (disabled wins). Explicit, not
// inherited: Typography sets its own colour, and native doesn't inherit.
const DropdownItemColorContext = createContext<ItemColor | undefined>(undefined);

const ItemColorProvider = ({ color, children }: { color: ItemColor; children: ReactNode }) => (
  <DropdownItemColorContext.Provider value={color}>{children}</DropdownItemColorContext.Provider>
);

const useItemColor = (): ItemColor =>
  useContext(DropdownItemColorContext) ?? "neutral-foreground-default";

/**
 * An item's text in the item's colour and label type (14/20: body-md on phones,
 * body-sm on desktop, split row 146), one line ending in "…". What `label`
 * renders; use it inside custom `children`.
 */
const DropdownItemText = ({ children, className }: DropdownItemTextProps) => (
  <Typography
    variant="body-md"
    color={useItemColor()}
    whiteSpace="nowrap"
    // `flex-1` is web-only: in the content-sized native menu it gave the text
    // no width of its own, so the menu didn't grow to fit it. `grow` keeps that
    // width and fills a wider row, which pushes a sub-trigger's caret to the end
    className={cn("web:flex-1 native:grow md:text-body-sm-dt shrink truncate", className)}
  >
    {children}
  </Typography>
);

DropdownItemText.displayName = "DropdownItemText";

/** An icon in the item's colour, for custom `children`. */
const DropdownItemIcon = ({
  name,
  size = iconSize.sm,
  weight,
  className,
}: DropdownItemIconProps) => (
  <Icon
    name={name}
    size={size}
    weight={weight}
    color={useItemColor()}
    className={cn("shrink-0", className)}
  />
);

DropdownItemIcon.displayName = "DropdownItemIcon";

const DropdownItem = ({
  label,
  onSelect,
  disabled = false,
  variant = "default",
  closeOnSelect,
  className,
  testID,
  accessibilityLabel,
  children,
}: DropdownItemProps) => (
  <DropdownMenuItemPrimitive
    onSelect={onSelect}
    disabled={disabled}
    closeOnSelect={closeOnSelect}
    className={cn(dropdownItemVariants({ disabled, variant }), className)}
    testID={testID}
    accessibilityLabel={getItemAccessibilityLabel({ label, accessibilityLabel, variant })}
  >
    <ItemColorProvider color={getItemColor(disabled, variant)}>
      {children ?? <DropdownItemText>{label}</DropdownItemText>}
    </ItemColorProvider>
  </DropdownMenuItemPrimitive>
);

DropdownItem.displayName = "DropdownItem";

const DropdownCheckboxItem = ({
  label,
  checked,
  onCheckedChange,
  disabled = false,
  closeOnSelect,
  className,
  testID,
  accessibilityLabel,
  children,
}: DropdownCheckboxItemProps) => (
  <DropdownMenuCheckboxItemPrimitive
    checked={checked}
    onCheckedChange={onCheckedChange}
    disabled={disabled}
    closeOnSelect={closeOnSelect}
    className={cn(dropdownItemVariants({ disabled }), "pl-spacing-2xl", className)}
    testID={testID}
    accessibilityLabel={getItemAccessibilityLabel({ label, accessibilityLabel })}
  >
    <DropdownMenuItemIndicatorPrimitive className="h-icon-sm w-icon-sm left-spacing-sm absolute flex items-center justify-center">
      {/* The item's colour, so a disabled item's check is attenuated too */}
      <Icon
        name="Check"
        size={iconSize.sm}
        weight="bold"
        color={getItemColor(disabled, "default")}
      />
    </DropdownMenuItemIndicatorPrimitive>
    <ItemColorProvider color={getItemColor(disabled, "default")}>
      {children ?? <DropdownItemText>{label}</DropdownItemText>}
    </ItemColorProvider>
  </DropdownMenuCheckboxItemPrimitive>
);

DropdownCheckboxItem.displayName = "DropdownCheckboxItem";

const DropdownRadioGroup = ({
  value,
  onValueChange,
  className,
  children,
}: DropdownRadioGroupProps) => (
  <DropdownMenuRadioGroupPrimitive
    value={value}
    onValueChange={onValueChange}
    className={className}
  >
    {children}
  </DropdownMenuRadioGroupPrimitive>
);

DropdownRadioGroup.displayName = "DropdownRadioGroup";

const DropdownRadioItem = ({
  label,
  value,
  disabled = false,
  closeOnSelect,
  className,
  testID,
  accessibilityLabel,
  children,
}: DropdownRadioItemProps) => (
  <DropdownMenuRadioItemPrimitive
    value={value}
    disabled={disabled}
    closeOnSelect={closeOnSelect}
    className={cn(dropdownItemVariants({ disabled }), "pl-spacing-2xl", className)}
    testID={testID}
    accessibilityLabel={getItemAccessibilityLabel({ label, accessibilityLabel })}
  >
    <DropdownMenuItemIndicatorPrimitive className="h-icon-sm w-icon-sm left-spacing-sm absolute flex items-center justify-center">
      {/* 8px, Badge's dot token. In the item's colour, so a disabled item's
          dot is attenuated too */}
      <Box
        className={cn(
          "rounded-radius-full size-badge-dot",
          disabled ? "bg-luce-neutral-foreground-attenuated" : "bg-luce-neutral-foreground-default",
        )}
      />
    </DropdownMenuItemIndicatorPrimitive>
    <ItemColorProvider color={getItemColor(disabled, "default")}>
      {children ?? <DropdownItemText>{label}</DropdownItemText>}
    </ItemColorProvider>
  </DropdownMenuRadioItemPrimitive>
);

DropdownRadioItem.displayName = "DropdownRadioItem";

const DropdownSeparator = ({ className }: DropdownSeparatorProps) => (
  <DropdownMenuSeparatorPrimitive className={cn(dropdownSeparatorClass, className)} />
);

DropdownSeparator.displayName = "DropdownSeparator";

const DropdownGroup = ({ children, className }: DropdownGroupProps) => (
  <DropdownMenuGroupPrimitive className={className}>{children}</DropdownMenuGroupPrimitive>
);

DropdownGroup.displayName = "DropdownGroup";

const DropdownSub = ({ children, ...rest }: DropdownSubProps) => (
  <DropdownMenuSubPrimitive {...rest}>{children}</DropdownMenuSubPrimitive>
);

DropdownSub.displayName = "DropdownSub";

const DropdownSubTrigger = ({
  label,
  disabled,
  className,
  testID,
  accessibilityLabel,
  children,
}: DropdownSubTriggerProps) => (
  <DropdownMenuSubTriggerPrimitive
    disabled={disabled}
    className={cn(dropdownItemVariants({ disabled }), className)}
    testID={testID}
    accessibilityLabel={getItemAccessibilityLabel({ label, accessibilityLabel })}
  >
    <ItemColorProvider color={getItemColor(disabled, "default")}>
      {children ?? <DropdownItemText>{label}</DropdownItemText>}
      {/* The sub-menu's caret (split row 151). The auto margin is web-only: on
          native it stretched the menu to the screen width */}
      <DropdownItemIcon name="CaretRight" className="web:ml-auto" />
    </ItemColorProvider>
  </DropdownMenuSubTriggerPrimitive>
);

DropdownSubTrigger.displayName = "DropdownSubTrigger";

const DropdownSubContent = ({ children, className }: DropdownSubContentProps) => (
  <DropdownMenuSubContentPrimitive className={cn(dropdownContentVariants(), className)}>
    {children}
  </DropdownMenuSubContentPrimitive>
);

DropdownSubContent.displayName = "DropdownSubContent";

export {
  DropdownRoot,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownItemIcon,
  DropdownItemText,
  DropdownCheckboxItem,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownSeparator,
  DropdownGroup,
  DropdownSub,
  DropdownSubTrigger,
  DropdownSubContent,
};
